import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ProductsPage.css'; // Make sure this path is correct
import { db } from './firebaseConfig'; // Your initialized Firestore instance
import { collection, query, where, getDocs, orderBy as firebaseOrderBy } from 'firebase/firestore';
import Header from "./components/Header";
import EarnWithUsModal from './components/EarnWithUsModal';
import CollectionSEO from './components/CollectionSEO';
// Import icons from lucide-react
import { Filter, IndianRupee, Loader2, XCircle, Clock } from 'lucide-react';
// ImageGallery component (paste above ProductsPage)
// Clean minimal ImageGallery (no thumbnails)

// Enhanced ImageGallery (cinematic + lightbox)

function ImageGallery({
  images = [],
  productName = "",
  lqip = "",                 // ✅ new
  autoplay = true,
  autoplayInterval = 4500,
  kenBurnsDuration = 12000,  // ms
}) {
  const imgs = Array.isArray(images) ? images.filter(Boolean) : [];
  const count = Math.max(1, imgs.length);
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [tiltStyle, setTiltStyle] = useState({});
  const autoplayRef = useRef(null);
  const containerRef = useRef(null);

  // autoplay
  useEffect(() => {
    if (!autoplay || count <= 1 || hover || lightboxOpen) return;
    autoplayRef.current = setInterval(() => setIndex(i => (i + 1) % count), autoplayInterval);
    return () => clearInterval(autoplayRef.current);
  }, [autoplay, autoplayInterval, count, hover, lightboxOpen]);

  useEffect(() => {
    setIndex(0); // reset when images change
  }, [images]);

  // keyboard for lightbox & arrows
  useEffect(() => {
    const onKey = (e) => {
      if (lightboxOpen) {
        if (e.key === "Escape") { setLightboxOpen(false); }
        if (e.key === "ArrowLeft") setIndex(i => (i - 1 + count) % count);
        if (e.key === "ArrowRight") setIndex(i => (i + 1) % count);
      } else {
        if (e.key === "ArrowLeft") setIndex(i => (i - 1 + count) % count);
        if (e.key === "ArrowRight") setIndex(i => (i + 1) % count);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, lightboxOpen]);

  // parallax tilt
  const onMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0].clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0].clientY);
    if (!clientX) return;
    const x = (clientX - rect.left) / rect.width - 0.5;
    const y = (clientY - rect.top) / rect.height - 0.5;
    const rotateY = x * 6; // adjust intensity
    const rotateX = -y * 6;
    const translateX = x * -6;
    setTiltStyle({
      transform: `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateX(${translateX}px)`,
    });
  };

  const onLeave = () => {
    setTiltStyle({ transform: "" });
  };

  const go = (i) => setIndex((i + count) % count);

  // Lightbox close with body scroll lock
  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? "hidden" : "";
  }, [lightboxOpen]);

  if (imgs.length === 0) {
    return (
      <div className="image-gallery cinematic fallback">
        <img src="https://placehold.co/400x500/cccccc/333?text=No+Image" alt={productName ? `${productName} - DOR Dress On Rent` : "Product - DOR Dress On Rent"} />
      </div>
    );
  }

  return (
    <>
      <div
        className="image-gallery cinematic"
        ref={containerRef}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); setTiltStyle({ transform: "" }); }}
        onMouseMove={onMove}
        onTouchMove={onMove}
        onTouchEnd={onLeave}
        style={tiltStyle}
        aria-label={`${productName} image gallery cinematic`}
      >
        <button className="ig-nav ig-prev" aria-label="Previous" onClick={() => go(index - 1)}>‹</button>

        <div
          className="ig-main cinematic-main"
          onDoubleClick={() => setLightboxOpen(true)}
          onClick={() => setLightboxOpen(true)}
          role="button"
          tabIndex={0}
        >
          {imgs.map((src, i) => {
            const active = i === index;
            return (
              <img
                key={i}
                src={src}
                alt={`${productName} - DOR Dress On Rent (${i + 1} of ${imgs.length})`}
                className={`ig-image cinematic ${active ? "active" : ""}`}
                style={{
                  animationDuration: `${kenBurnsDuration}ms`,
                  backgroundImage: lqip ? `url(${lqip})` : undefined,   // ✅ blur placeholder
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
                loading="lazy"            // ✅ lazy load
                decoding="async"
                draggable={false}
                onError={(e) => (e.target.src = "https://placehold.co/800x1000/cccccc/333?text=Image+Not+Found")}
              />
            );
          })}
          {/* overlays for film grain + vignette + bloom */}
          <div className="cinematic-overlay" aria-hidden></div>
        </div>

        <button className="ig-nav ig-next" aria-label="Next" onClick={() => go(index + 1)}>›</button>
      </div>

      {/* Lightbox modal */}
      {lightboxOpen && (
        <div className="ig-lightbox" role="dialog" aria-modal="true" aria-label="Image viewer" onClick={() => setLightboxOpen(false)}>
          <div className="ig-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="lb-close" onClick={() => setLightboxOpen(false)} aria-label="Close">✕</button>
            <button className="lb-arrow lb-prev" onClick={() => go(index - 1)} aria-label="Previous">‹</button>
            <div className="lb-image-wrap">
              <img src={imgs[index]} alt={`${productName} - Large View - DOR Dress On Rent`} className="lb-image" />
              <div className="lb-caption">{productName} — {index + 1} / {imgs.length}</div>
            </div>
            <button className="lb-arrow lb-next" onClick={() => go(index + 1)} aria-label="Next">›</button>
          </div>
        </div>
      )}
    </>
  );
}




function ProductsPage() {
  const { gender, subcategoryName } = useParams();

  const [products, setProducts] = useState([]);
  const [categoryData, setCategoryData] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError] = useState('');

  // Filter states
  // Initialize selectedStore from localStorage, defaulting to 'All' if not found
  const [selectedStore, setSelectedStore] = useState(() => {
    const storedStore = localStorage.getItem('selectedStore');
    return storedStore || 'All';
  });
  const [selectedColor, setSelectedColor] = useState('All');
  const [selectedSize, setSelectedSize] = useState('All');
  const [sortBy, setSortBy] = useState('rentAsc'); // 'rentAsc', 'rentDesc', 'newest'

  // State for showing/hiding mobile filters
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showEarnModal, setShowEarnModal] = useState(false);

  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const toggleTheme = () => {
    setTheme(currentTheme => {
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', newTheme); // Save the new theme choice
      return newTheme;
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  // New states for dynamic filter options
  const [dynamicStores, setDynamicStores] = useState([]);
  const [dynamicColors, setDynamicColors] = useState([]);
  const [dynamicSizes, setDynamicSizes] = useState([]);
  const storedTheme = localStorage.getItem("theme") || "light";


  // --- Fetch Dynamic Filter Options from Firebase ---
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch Stores from 'filterOptions/stores/list' collection
        const storesSnapshot = await getDocs(collection(db, 'filterOptions', 'stores', 'list'));
        // Map document IDs to store names. If you have a 'name' field in documents, use doc.data().name
        const fetchedStores = storesSnapshot.docs.map(doc => doc.id);
        setDynamicStores(['All', ...fetchedStores]); // Add 'All' option

        // Fetch Colors from 'filterOptions/colors/list' collection
        const colorsSnapshot = await getDocs(collection(db, 'filterOptions', 'colors', 'list'));
        const fetchedColors = colorsSnapshot.docs.map(doc => doc.id);
        setDynamicColors(['All', ...fetchedColors]); // Add 'All' option

        // Fetch Sizes from 'filterOptions/sizes/list' collection
        const sizesSnapshot = await getDocs(collection(db, 'filterOptions', 'sizes', 'list'));
        const fetchedSizes = sizesSnapshot.docs.map(doc => doc.id);
        setDynamicSizes(['All', ...fetchedSizes]); // Add 'All' option

      } catch (error) {
        console.error("Error fetching filter options:", error);
        // In case of error, filters might appear empty or non-functional.
        // Consider adding default hardcoded values here as a fallback if needed for production.
      }
    };

    fetchFilterOptions();
  }, []); // Empty dependency array means this runs once on component mount

  // --- Firebase Data Fetching for Products (remains largely the same) ---
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      setProductError('');
      setProducts([]); // Clear previous products
      setCategoryData(null);

      if (!gender || !subcategoryName || (gender !== 'men' && gender !== 'women')) {
        setProductError("Invalid category or subcategory selected.");
        setLoadingProducts(false);
        return;
      }

      try {
        const parentCollection = gender === 'men' ? 'menCategories' : 'womenCategories';

        const subcategoryQuery = query(
          collection(db, parentCollection),
          where('name', '==', subcategoryName)
        );
        const subcategorySnapshot = await getDocs(subcategoryQuery);

        if (subcategorySnapshot.empty) {
          setProductError(`Subcategory "${subcategoryName}" not found.`);
          setLoadingProducts(false);
          return;
        }

        const subcategoryDoc = subcategorySnapshot.docs[0];
        const subcategoryDocId = subcategoryDoc.id;
        setCategoryData(subcategoryDoc.data());

        let productsCollectionRef = collection(db, parentCollection, subcategoryDocId, 'products');

        let queriesToExecute = [];

        // 1. Build the base query with non-array-contains filters and sorting
        let currentBaseQuery = productsCollectionRef;

        // Apply 'color' filter (this is an equality filter, so it can be combined with one array-contains)
        if (selectedColor !== 'All') {
          currentBaseQuery = query(currentBaseQuery, where('color', '==', selectedColor));
        }

        // Apply sorting
        if (sortBy === 'rentAsc') {
          currentBaseQuery = query(currentBaseQuery, firebaseOrderBy('rent', 'asc'));
        } else if (sortBy === 'rentDesc') {
          currentBaseQuery = query(currentBaseQuery, firebaseOrderBy('rent', 'desc'));
        } else if (sortBy === 'newest') {
          currentBaseQuery = query(currentBaseQuery, firebaseOrderBy('addedDate', 'desc'));
        }

        // 2. Conditionally add array-contains queries to the list of queries to execute
        // Each array-contains filter will trigger a separate query if active
        if (selectedStore !== 'All') {
          queriesToExecute.push(query(currentBaseQuery, where('availableStores', 'array-contains', selectedStore)));
        }

        if (selectedSize !== 'All') {
          queriesToExecute.push(query(currentBaseQuery, where('sizes', 'array-contains', selectedSize)));
        }

        // 3. If no array-contains filters were applied, just run the base query
        if (queriesToExecute.length === 0) {
          queriesToExecute.push(currentBaseQuery);
        }

        let allProducts = [];
        const seenProductIds = new Set(); // To prevent duplicate products from multiple queries

        // 4. Execute all generated queries and merge results
        for (const q of queriesToExecute) {
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            if (!seenProductIds.has(doc.id)) {
              allProducts.push({ id: doc.id, ...doc.data() });
              seenProductIds.add(doc.id);
            }
          });
        }

        setProducts(allProducts);

      } catch (error) {
        console.error("Error fetching products:", error);
        setProductError("Failed to load products. Please try again later.");
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [gender, subcategoryName, selectedStore, selectedColor, selectedSize, sortBy]); // Dependencies for useEffect

  // Handlers for filter/sort changes
  const handleStoreChange = (e) => setSelectedStore(e.target.value);
  const handleColorChange = (e) => setSelectedColor(e.target.value);
  const handleSizeChange = (e) => setSelectedSize(e.target.value);
  const handleSortByChange = (e) => setSortBy(e.target.value);

  // Clear all filters
  const clearFilters = () => {
    setSelectedStore('All');
    setSelectedColor('All');
    setSelectedSize('All');
    setSortBy('rentAsc');
    // Optionally, remove the selected store from localStorage when filters are cleared
    localStorage.removeItem('selectedStore');
  };

  // Toggle mobile filters visibility
  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  return (
    <div className={`products-page home-page ${storedTheme}-theme`}>
      <CollectionSEO
        gender={gender}
        subcategoryName={subcategoryName}
        categoryImage={categoryData?.imageUrl || categoryData?.image}
        products={products}
        loading={loadingProducts}
        error={productError}
      />
      <EarnWithUsModal
        showEarnModal={showEarnModal}
        setShowEarnModal={setShowEarnModal}
        theme={theme}
      />
      <Header
        theme={theme}
        toggleTheme={toggleTheme}
        scrolled={scrolled}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        setShowEarnModal={setShowEarnModal}
      />

      <header className="products-header">
        <div className="products-header-content">
          <h1 className="products-title">
            {gender === 'men' ? "Men's " : gender === 'women' ? "Women's " : ""}
            {subcategoryName} on Rent
          </h1>
          <p style={{ maxWidth: "700px", margin: "0.5rem auto 1.5rem", opacity: 0.9, lineHeight: 1.5, fontSize: "1rem" }}>
            Explore our exclusive collection of {subcategoryName} for {gender === 'men' ? "Men" : "Women"}, available for rent from DOR Dress On Rent. Enjoy premium designer wear, professionally dry-cleaned and altered for your perfect fit.
          </p>

          <p className="products-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>

            <span className="breadcrumb-separator"> &gt; </span>

            <Link to={`/#${gender}`} className="breadcrumb-link">
              {gender === 'men' ? 'Men' : 'Women'}
            </Link>

            <span className="breadcrumb-separator"> &gt; </span>

            {subcategoryName}
          </p>
        </div>
      </header>

      {/* Horizontal Filter Bar */}
      <div className="filter-bar-wrapper">
        <div className="filter-bar">
          <button className="mobile-filter-toggle" onClick={toggleMobileFilters}>
            <Filter size={18} /> <span className="toggle-text">Filters</span>
          </button>

          <div className="filter-group desktop-filter">
            <label htmlFor="store-select">Store:</label>
            <select id="store-select" value={selectedStore} onChange={handleStoreChange} className="filter-select">
              {/* Dynamically populated store options */}
              {dynamicStores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </div>

          <div className="filter-group desktop-filter">
            <label htmlFor="color-select">Color:</label>
            <select id="color-select" value={selectedColor} onChange={handleColorChange} className="filter-select">
              {/* Dynamically populated color options */}
              {dynamicColors.map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
          </div>

          <div className="filter-group desktop-filter">
            <label htmlFor="size-select">Size:</label>
            <select id="size-select" value={selectedSize} onChange={handleSizeChange} className="filter-select">
              {/* Dynamically populated size options */}
              {dynamicSizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="filter-group desktop-filter">
            <label htmlFor="sort-by-select">Sort By:</label>
            <select id="sort-by-select" value={sortBy} onChange={handleSortByChange} className="filter-select">
              <option value="rentAsc">Rent: Low to High</option>
              <option value="rentDesc">Rent: High to Low</option>
              <option value="newest">Newest Arrivals</option>
            </select>
          </div>

          <button className="clear-filters-btn desktop-filter" onClick={clearFilters}>
            <XCircle size={18} /> Clear
          </button>
        </div>
      </div>

      {/* Main Content (Product Listing) */}
      <div className="products-content-wrapper">
        {/* Mobile Filter Overlay */}
        <aside className={`products-filters-overlay ${showMobileFilters ? 'show' : ''}`}>
          <div className="filters-header">
            <h2 className="filters-title"><Filter size={24} className="icon-mr" /> Filters & Sort</h2>
            <button className="close-filters-btn" onClick={toggleMobileFilters}>
              &times;
            </button>
          </div>

          <div className="filter-group">
            <label htmlFor="store-select-overlay">Store Location:</label>
            <select id="store-select-overlay" value={selectedStore} onChange={handleStoreChange} className="filter-select">
              {/* Dynamically populated store options */}
              {dynamicStores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="color-select-overlay">Color:</label>
            <select id="color-select-overlay" value={selectedColor} onChange={handleColorChange} className="filter-select">
              {/* Dynamically populated color options */}
              {dynamicColors.map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="size-select-overlay">Size:</label>
            <select id="size-select-overlay" value={selectedSize} onChange={handleSizeChange} className="filter-select">
              {/* Dynamically populated size options */}
              {dynamicSizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-by-select-overlay">Order:</label>
            <select id="sort-by-select-overlay" value={sortBy} onChange={handleSortByChange} className="filter-select">
              <option value="rentAsc">Rent: Low to High</option>
              <option value="rentDesc">Rent: High to Low</option>
              <option value="newest">Newest Arrivals</option>
            </select>
          </div>
          <button className="clear-filters-btn-overlay" onClick={clearFilters}>
            <XCircle size={20} className="icon-mr" /> Clear All Filters
          </button>
        </aside>

        <main className="products-listing">
          {loadingProducts ? (
            <div className="message-container loading">
              <Loader2 size={48} className="animate-spin text-blue-500" />
              <p className="message-text">Fetching amazing products...</p>
            </div>
          ) : productError ? (
            <p className="message-container error">
              <XCircle size={24} className="icon-mr" /> {productError}
            </p>
          ) : products.length === 0 ? (
            <div className="message-container no-products-found">
              <Clock size={60} className="no-products-icon" /> {/* Changed icon to Clock */}
              <p className="no-products-title">We will be coming soon!</p>
              <p className="no-products-text">
                Get ready for an exciting collection! We're busy preparing something special for you.
              </p>
              {/* Removed the clear filters button for this message */}
            </div>
          ) : (
            <div className="product-grid">
              {products.map(product => (
                <Link
                  to={`/product/${gender}/${subcategoryName}/${product.id}`}
                  key={product.id}
                  className="product-card-link"
                >
                  <div className="product-card">
                    <div className="product-image-container">
                      {(() => {
                        const hero =
                          product?.thumbs?.sm?.url ||
                          product?.thumbs?.xs?.url ||
                          product?.imageUrl;
                        return (
                          <ImageGallery
                            images={[hero, ...(product.images || [])]}   // ✅ use thumb first
                            lqip={product?.lqip}                         // ✅ blur placeholder
                            productName={product.name}
                            autoplay={true}
                            autoplayInterval={5000}
                            kenBurnsDuration={12000}
                          />
                        );
                      })()}
                    </div>



                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-color">Store: {product.availableStores ? product.availableStores.join(', ') : 'N/A'}</p>
                      {/* <p className="product-color">Available Sizes: {product.sizes ? product.sizes.join(', '):'N/A' }</p> */}

                      <p className="product-rent">
                        <IndianRupee size={14} className="inline-icon" />{product.rent.toLocaleString('en-IN')}
                        <span className="rent-per-day"> for 3 days</span>
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ProductsPage;